import { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { NAVY } from '../styles/ds';
import ProposalCard from './ProposalCard';
import { useAssistant } from './AssistantContext';

// Shared message-list + input, used identically by the compact panel
// (AssistantWidget.js) and the full page (AssistantPage.js) — one render
// path, so the two views can never drift. `variant` only tunes container
// sizing/padding, never behavior.
const AssistantConversation = ({ variant = 'full' }) => {
  const { messages, sending, sendMessage } = useAssistant();
  const [input, setInput] = useState('');

  const isCompact = variant === 'compact';

  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const ok = await sendMessage(text);
    // Failed send (network error or non-OK response) — restore the typed
    // text instead of silently losing it.
    if (!ok) setInput(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, boxSizing: 'border-box' }}>
      {/* ── Message list ──────────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5,
        p: isCompact ? 1.5 : 0, pb: 2,
      }}>
        {messages.length === 0 && (
          <Typography color="text.secondary" fontSize={13} textAlign="center" mt={4}>
            עדיין אין הודעות — התחל בשאלה
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box className="crm-assistant-message"
            key={i}
            sx={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: isCompact ? '90%' : '75%',
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                px: 2, py: 1.25,
                borderRadius: 2,
                backgroundColor: m.role === 'user' ? NAVY : '#fff',
                color: m.role === 'user' ? '#fff' : 'inherit',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                whiteSpace: 'pre-wrap',
                fontSize: isCompact ? 13 : 14,
              }}
            >
              {m.content}
            </Box>
            {m.proposal && <ProposalCard proposal={m.proposal} />}
          </Box>
        ))}
        {sending && (
          <Box sx={{ alignSelf: 'flex-start' }}>
            <CircularProgress size={18} sx={{ color: NAVY }} />
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* ── Input ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', gap: 1, alignItems: 'flex-end', pt: 1,
        px: isCompact ? 1.5 : 0, pb: isCompact ? 1.5 : 0,
        borderTop: '1px solid rgba(0,0,0,0.08)',
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="הקלד הודעה..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          size="small"
          sx={{ backgroundColor: '#fff', borderRadius: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !input.trim()}
          sx={{ backgroundColor: NAVY, borderRadius: 2, minWidth: 48, height: 40, '&:hover': { backgroundColor: '#162d5a' } }}
        >
          <SendIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};

export default AssistantConversation;
